import { Injectable } from '@angular/core';
import { ERROR_CODES } from '../constants/fhir.constants';
import type { ConversionError } from '../interfaces/fhir-converter.interfaces';
import type { FHIRQuestionnaireResponseAnswer, FHIRQuestionnaireResponseItem } from '../models/fhir.model';

/** Valid answer value[x] property names on FHIRQuestionnaireResponseAnswer. */
const ANSWER_VALUE_KEYS = [
  'valueBoolean',
  'valueDecimal',
  'valueInteger',
  'valueDate',
  'valueDateTime',
  'valueTime',
  'valueString',
  'valueUri',
  'valueCoding',
  'valueQuantity',
  'valueAttachment',
  'valueReference',
] as const;

/** Valid QuestionnaireResponse status values (FHIR R4). */
const QR_VALID_STATUSES = ['in-progress', 'completed', 'amended', 'entered-in-error', 'stopped'] as const;

/**
 * FHIR R4 QuestionnaireResponse Validator
 *
 * Implements FHIR R4 QuestionnaireResponse constraints:
 *
 * - **qrs-1** (Rule on item): Nested item can't be beneath both item and answer — `(answer.exists() and
 *   item.exists()).not()`
 * - Required fields: `status` (1..1), `item.linkId` (1..1)
 * - Status value-set conformance
 * - Answer value[x] validation — at most one value per answer
 * - Recursive item validation
 */
@Injectable({ providedIn: 'root' })
export class FhirQuestionnaireResponseValidator {
  /**
   * Validate a FHIR QuestionnaireResponse resource.
   *
   * @param resource - The parsed resource object.
   * @param errors - Array to collect errors into.
   * @param warnings - Array to collect warnings into.
   * @returns `true` when the resource has no errors.
   */
  validate(resource: unknown, errors: ConversionError[] = [], warnings: string[] = []): boolean {
    const r = resource as Record<string, unknown>;

    // Validate resourceType
    if (r['resourceType'] !== 'QuestionnaireResponse') {
      errors.push({
        code: ERROR_CODES.INVALID_RESOURCE_TYPE,
        message: 'resourceType must be "QuestionnaireResponse"',
        severity: 'error',
      });
    }

    // Validate status
    this.validateStatus(r, errors);

    // Informational: questionnaire reference
    if (!r['questionnaire']) {
      warnings.push('QuestionnaireResponse does not reference a Questionnaire via the "questionnaire" element');
    }

    // Validate items
    const items = r['item'] as FHIRQuestionnaireResponseItem[] | undefined;
    if (items && Array.isArray(items)) {
      this.validateItems(items, errors, warnings, '');
    }

    return !errors.some((e) => e.severity === 'error');
  }

  // ------------------------------------------------------------------
  // Private helpers
  // ------------------------------------------------------------------

  /** Validate status field. */
  private validateStatus(r: Record<string, unknown>, errors: ConversionError[]): void {
    const status = r['status'];
    if (!status) {
      errors.push({
        code: ERROR_CODES.MISSING_STATUS,
        message: 'status is required',
        severity: 'error',
      });
      return;
    }

    if (!QR_VALID_STATUSES.includes(status as (typeof QR_VALID_STATUSES)[number])) {
      errors.push({
        code: ERROR_CODES.INVALID_STATUS,
        message: `Invalid status "${status}". Must be one of: ${QR_VALID_STATUSES.join(', ')}`,
        path: 'status',
        severity: 'error',
      });
    }
  }

  /** Recursively validate QuestionnaireResponse items. */
  private validateItems(
    items: FHIRQuestionnaireResponseItem[],
    errors: ConversionError[],
    warnings: string[],
    parentPath: string
  ): void {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const path = parentPath ? `${parentPath}.item[${i}]` : `item[${i}]`;

      // linkId is required (1..1)
      if (!item.linkId) {
        errors.push({
          code: ERROR_CODES.QRS_MISSING_LINKID,
          message: 'QuestionnaireResponse item is missing required linkId',
          path,
          severity: 'error',
        });
      }

      // qrs-1: (answer.exists() and item.exists()).not()
      // An item cannot have BOTH direct answers and direct nested items.
      const hasAnswers = item.answer !== undefined && item.answer.length > 0;
      const hasItems = item.item !== undefined && item.item.length > 0;
      if (hasAnswers && hasItems) {
        errors.push({
          code: ERROR_CODES.QRS_1_ANSWER_AND_ITEM,
          message: `qrs-1: Item "${item.linkId ?? i}" cannot have both "answer" and nested "item" — use answer.item for nested items under an answer`,
          path,
          severity: 'error',
        });
      }

      // Validate answers
      if (item.answer) {
        for (let a = 0; a < item.answer.length; a++) {
          this.validateAnswer(item.answer[a], `${path}.answer[${a}]`, errors, warnings);

          // Recursively validate nested items under each answer
          if (item.answer[a].item && Array.isArray(item.answer[a].item)) {
            this.validateItems(item.answer[a].item!, errors, warnings, `${path}.answer[${a}]`);
          }
        }
      }

      // Recursively validate direct nested items
      if (item.item && Array.isArray(item.item)) {
        this.validateItems(item.item, errors, warnings, path);
      }
    }
  }

  /** Validate a single answer — at most one value[x] property should be set. */
  private validateAnswer(
    answer: FHIRQuestionnaireResponseAnswer,
    path: string,
    errors: ConversionError[],
    _warnings: string[]
  ): void {
    const a = answer as Record<string, unknown>;
    const setKeys = ANSWER_VALUE_KEYS.filter((k) => a[k] !== undefined && a[k] !== null);

    if (setKeys.length > 1) {
      errors.push({
        code: ERROR_CODES.QRS_INVALID_ANSWER_VALUE,
        message: `Answer has multiple value[x] properties set (${setKeys.join(', ')}). Only one is permitted.`,
        path,
        severity: 'error',
      });
    }
  }
}
